import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { memo } from "react";
import ReactModal from "react-modal";

import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import { ReactComponent as PinIcon } from "assets/icons/pin.svg";
import { useGetNotices } from "hooks/apis/notice";
import format from "lib/format";
import { Notice as NoticeType } from "types/types";
import NoticeDetailModal from "./NoticeDetailModal";
import Pagination from "components/Pagination";
import { FormProvider, useForm } from "react-hook-form";

const size = 20;

type FormValues = {
  page: number;
};

const Notice = () => {
  const modal = useModal();
  const methods = useForm<FormValues>();
  const page = methods.watch("page");
  const { data, totalItems, currentPage } = useGetNotices({
    type: "notice",
    page,
    size,
  });
  const queryClient = useQueryClient();

  const closeModal = () => {
    modal.hide();
    modal.reject();
  };

  const openNoticeDetail = (notice: NoticeType) => {
    closeModal();
    setTimeout(() => {
      NiceModal.show(NoticeDetailModal, { noticeId: notice.id });
      if (!notice.isViewed) {
        queryClient.invalidateQueries({ queryKey: ["notices"] });
      }
    }, 100);
  };

  return (
    <ReactModal
      overlayClassName="overflow-auto flex fixed size-full top-0 left-0 z-30"
      className="bg-[rgba(0,0,0,0.2)] min-w-[1280px] min-h-[720px] flex justify-center items-center size-full outline-none"
      isOpen={modal.visible}
      onRequestClose={closeModal}
      onAfterClose={() => {
        modal.remove();
      }}
    >
      <FormProvider {...methods}>
        <main className="bg-white flex flex-col w-[50rem] h-[37.5rem] p-[1.875rem] rounded-md">
          <header className="flex justify-end w-full relative">
            <h2 className="absolute text-center inset-0 justify-self-center self-center font-bold text-24 leading-[1.375rem]">
              공지사항
            </h2>
            <button
              className="relative z-10"
              type="button"
              onClick={closeModal}
            >
              <CloseIcon className="size-[2.25rem]" />
            </button>
          </header>
          <section className="flex flex-col flex-1 overflow-auto pt-[2.625rem] gap-[0.625rem]">
            <span className="text-16 text-secondary">
              총 {(totalItems ?? 0).toLocaleString()}건
            </span>
            <table className="flex flex-col overflow-hidden">
              <thead className="text-16 text-white w-full">
                <tr className="flex">
                  <th className="h-14 flex items-center justify-center w-[5.875rem] bg-secondary rounded-l-md">
                    번호
                  </th>
                  <th className="h-14 flex items-center justify-center w-[5.875rem] bg-secondary">
                    유형
                  </th>
                  <th className="h-14 flex items-center flex-1 bg-secondary px-5 text-left">
                    제목
                  </th>
                  <th className="h-14 flex items-center justify-center w-[5.875rem] bg-secondary">
                    등록일
                  </th>
                  <th className="h-14 flex items-center justify-center w-[5.875rem] bg-secondary rounded-r-md">
                    확인
                  </th>
                </tr>
              </thead>
              <tbody className="flex-1 overflow-auto w-full text-center text-secondary font-medium text-16">
                {data?.map((notice, index) => (
                  <tr key={notice.id} className="flex border-b border-gray">
                    <td className="h-14 flex items-center justify-center w-[5.875rem]">
                      {notice.isFixed ? (
                        <PinIcon />
                      ) : (
                        totalItems - (index + 20 * (currentPage - 1))
                      )}
                    </td>
                    <td className="h-14 flex items-center justify-center w-[5.875rem]">
                      {notice.type}
                    </td>
                    <td className="h-14 flex items-center flex-1 text-left px-5">
                      <button
                        type="button"
                        onClick={() => openNoticeDetail(notice)}
                        className="line-clamp-1 break-all hover:underline duration-100"
                      >
                        {notice.title}
                      </button>
                    </td>
                    <td className="h-14 flex items-center justify-center w-[5.875rem]">
                      {format(notice.createdAt, "yyyy/MM/dd")}
                    </td>
                    <td
                      className={clsx(
                        "text-14 h-14 flex items-center justify-center w-[5.875rem]",
                        {
                          "text-red": !notice.isViewed,
                        },
                      )}
                    >
                      {notice.isViewed ? "확인완료" : "확인전"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination total={totalItems} />
          </section>
        </main>
      </FormProvider>
    </ReactModal>
  );
};

const NoticeModal = NiceModal.create(memo(Notice));
NoticeModal.displayName = "NoticeModal";

export default NoticeModal;
